import React, { useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../context/StateContext";
import { Branch } from "../../../models/branch";
import { Searches } from "../../../models/shipeezi.table";
import BranchService from "../../../services/branch.service";
import { Color } from "../../Form/Button";
import Modal from "../../Generic/Modal";
import EditBranchModal from "./EditBranchModal";
import { ContentSection, EditIcon } from "./styles";

export interface Props {
  open: boolean;
  total: number;
  closeModal: () => void;
}

const BranchesModal = ({ open, total, closeModal }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(5);
  const [searches, setSearches] = useState<Searches>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [branchToEdit, setBranchToEdit] = useState<Branch>();

  const { isLoading, isFetching, refetch } = useQuery(
    ["onboarding.branch.list", page, pageSize, searches],
    () =>
      BranchService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setBranches(response.data);
      },
    }
  );

  const columns: Column[] = [
    {
      name: "name",
      title: translate("branchName"),
    },
    {
      name: "code",
      title: translate("code"),
    },
    {
      name: "description",
      title: translate("description"),
    },
  ];

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleEditButton = (id: string | number, branch: Branch) => {
    return (
      <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
        <div>
          <EditIcon
            onClick={() => {
              setOpenModalEdit(true);
              setBranchToEdit(branch);
            }}
            color="#475467"
            size={18}
          />
        </div>
      </Tooltip>
    );
  };

  return (
    <>
      <Modal
        open={open}
        title={translate("branches")}
        handleClose={closeModal}
        data-cy="onboardingBranchesModal"
        dataCyTitle="onboardingBranchesModalTitle"
        showActionButtons={true}
        actionButtonProps={{
          textCancel: translate("close"),
          customColorCancel: Color.TEXT_INFO,
          dataCyCancel: "closeButton",
          onCancel: () => {
            closeModal();
          },
        }}
      >
        <ContentSection>
          <ShipeeziGrid
            data={branches}
            columns={columns}
            totalItems={total}
            loadingData={isLoading || isFetching}
            emptyData={translate("noInformationToBeShown")}
            pageSize={pageSize}
            pageSizeList={[5, 10, 15, 100]}
            positionActionsColumn="right"
            onChangePageSize={handlePageSize}
            currentPage={page}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
            showFooterPageInfo={true}
            showFooterGrid={true}
            rowActionsExtra={[
              {
                title: translate("edit"),
                key: "edit",
                buttonAndAction: handleEditButton,
              },
            ]}
            generalActions={{
              orderBy: {
                left: ["handleSearch"],
              },
              handleSearch: {
                onSearch: (filters) => handleSearch(filters),
              },
            }}
          />
        </ContentSection>
      </Modal>
      {openModalEdit && branchToEdit && (
        <EditBranchModal
          branch={branchToEdit}
          closeModal={() => setOpenModalEdit(false)}
          open={openModalEdit}
          refetch={() => refetch()}
        />
      )}
    </>
  );
};

export default BranchesModal;
